import { ITimeOccurrenceDatatype, IRawTimeOccurrenceDatatype } from '../../datastore/time/data_types';
import { Duration } from '../data_types';
import { PhxCalendarConfiguration } from '../../config/calendar_configuration';
import { TimeStartEndIdentifierEnum } from '../../engines/time/time_types';
import { IDayOfTheWeekValue, IDayOfTheWeekEnum } from 'utility-js-time';
import * as _4 from 'lodash4';
import { BusyTimeErrorMessage } from '../../strategies/busy_time_strategy';
import { IChangedBusyTimeDatasource } from '../../datastore/time/changed_busy_time_datasource';
import { IChangedBusyTime } from '../../builders/time/changed_busy_time_builder';

export class TimeOccurrenceServant {
  constructor(
    private TimeOccurrenceDTD: ITimeOccurrenceDatatype,
    private Interface: any,
    private ngPhxCalendarConfiguration: PhxCalendarConfiguration,
    private TimeOccurrenceBuilder: any,
    private DayOfTheWeekEnum: IDayOfTheWeekEnum
  ) {}

  // Given a TimeOccurrence, take a property object and use that to return a raw TimeOccurrence (not DTO) object.
  copyTimeOccurrenceWithProperties(
    timeOccurrence: ITimeOccurrenceDatatype,
    propertyObj: Object
  ): ITimeOccurrenceDatatype {
    let rawTimeOccurrenceData: any = {
      appId: undefined,
      day_of_week: timeOccurrence.day._ordinal,
      from_tm: moment(timeOccurrence.from).format('HH:mm:ss'),
      to_tm: moment(timeOccurrence.to).format('HH:mm:ss'),
      repeats_every: timeOccurrence.repeats_every,
      trackingId: timeOccurrence.trackingId,
      beginMinutes: timeOccurrence.beginMinutes,
      endMinutes: timeOccurrence.endMinutes,
      bkgColorClass: timeOccurrence.bkgColorClass,
      content: timeOccurrence.content,
      group: timeOccurrence.group,
      type: timeOccurrence.type,
      isLocked: timeOccurrence.isLocked,
      isWarning: timeOccurrence.isWarning,
      isPreview: timeOccurrence.isPreview,
      isWaitlisted: timeOccurrence.isWaitlisted,
      isRegistered: timeOccurrence.isRegistered,
      icons: timeOccurrence.icons,
      hover: timeOccurrence.hover,
      isSwapFrom: timeOccurrence.isSwapFrom,
      isSwapTo: timeOccurrence.isSwapTo
    };

    _.forEach(propertyObj, function (value: any, key: string): void {
      rawTimeOccurrenceData[key] = value;
    });

    return new this.TimeOccurrenceBuilder(rawTimeOccurrenceData);
  }

  getTimeOccurrenceDuration(timeOccurrence: ITimeOccurrenceDatatype): Duration {
    return timeOccurrence.to.diff(timeOccurrence.from);
  }

  isTimeOccurrenceValid(timeOccurrence: ITimeOccurrenceDatatype): boolean {
    return (
      !!this.isTimeOccurrenceInBeginAndEndWindow(timeOccurrence) &&
      !!this.isTimeOccurrenceStartBeforeEnd(timeOccurrence)
    );
  }

  isTimeOccurrenceOnWeekend(timeOccurrence: ITimeOccurrenceDatatype): boolean {
    return this.DayOfTheWeekEnum.SA.isEqual(timeOccurrence.day) || this.DayOfTheWeekEnum.SU.isEqual(timeOccurrence.day);
  }

  isTimeOccurrenceInBeginWindow(timeOccurrence: ITimeOccurrenceDatatype): boolean {
    const configFromHours: number = parseInt(this.ngPhxCalendarConfiguration.hourBegin, 10);

    let fromMomentObj: moment.Moment = timeOccurrence.from.clone();
    fromMomentObj.set({ hour: configFromHours, minutes: 0 });

    return timeOccurrence.from.isSameOrAfter(fromMomentObj);
  }

  isTimeOccurrenceInEndWindow(timeOccurrence: ITimeOccurrenceDatatype): boolean {
    const configToHours: number = parseInt(this.ngPhxCalendarConfiguration.hourEnd, 10) + 1;

    let toMomentObj: moment.Moment = timeOccurrence.to.clone();
    toMomentObj.set({ hour: configToHours, minutes: 0 });

    return timeOccurrence.to.isSameOrBefore(toMomentObj);
  }

  isTimeOccurrenceInBeginAndEndWindow(timeOccurrence: ITimeOccurrenceDatatype): boolean {
    return this.isTimeOccurrenceInBeginWindow(timeOccurrence) && this.isTimeOccurrenceInEndWindow(timeOccurrence);
  }

  isTimeOccurrenceStartBeforeEnd(timeOccurrence: ITimeOccurrenceDatatype): boolean {
    return timeOccurrence.from.isSameOrBefore(timeOccurrence.to);
  }

  isTimeInSwap(timeOccurrence: ITimeOccurrenceDatatype): boolean {
    if (!timeOccurrence) {
      return false;
    }
    return timeOccurrence.isSwapFrom || timeOccurrence.isSwapTo;
  }

  shiftTimeOccurrenceToBound(
    timeOccurrence: ITimeOccurrenceDatatype,
    timeBoundary: TimeStartEndIdentifierEnum,
    timeBoundaryValue: number
  ): ITimeOccurrenceDatatype {
    const duration: Duration = this.getTimeOccurrenceDuration(timeOccurrence);

    // TODO moment wants a number, but our typings are outdated so Typescript wants a string
    const newTimeBoundary: moment.Moment =
      timeBoundary === TimeStartEndIdentifierEnum.End
        ? moment(timeBoundaryValue as any, 'hh').subtract(duration, 'ms')
        : moment(timeBoundaryValue as any, 'hh').add(duration, 'ms');

    let newTimeObj: Object =
      timeBoundary === TimeStartEndIdentifierEnum.End
        ? { from_tm: newTimeBoundary.format('HH:mm:ss'), to_tm: timeBoundaryValue }
        : { from_tm: timeBoundaryValue, to_tm: newTimeBoundary.format('HH:mm:ss') };

    let newTime: ITimeOccurrenceDatatype = this.copyTimeOccurrenceWithProperties(timeOccurrence, newTimeObj);

    return newTime;
  }

  getNewTime(changedBusyTimeDatasource: IChangedBusyTimeDatasource) {
    return _4.find(changedBusyTimeDatasource.read(), (item: any) => item.is_new_time === true) || {};
  }

  getUpdatedTimes(changedBusyTimeDatasource: IChangedBusyTimeDatasource) {
    return _4.find(changedBusyTimeDatasource.read(), (item: any) => item.is_new_time === false);
  }

  momentToString(momentTimeObj: any) {
    return momentTimeObj.format('HH:mm');
  }

  validatePotentialTime(
    potentialTime: IRawTimeOccurrenceDatatype | IChangedBusyTime,
    errors: { found: any[] },
    timeRange: any
  ): any[] {
    _4.remove(errors.found, undefined);

    const toSimpleMilitaryTime = (timeString: string): number => parseInt(moment(timeString, 'hh:mm a').format('HHmm'));
    const isStartTimeAfterEndTime = (timeObj: IRawTimeOccurrenceDatatype | IChangedBusyTime) => {
      let from: string = !_4.isString(timeObj.from_tm) ? this.momentToString(timeObj.from_tm) : timeObj.from_tm;
      let to: string = !_4.isString(timeObj.to_tm) ? this.momentToString(timeObj.to_tm) : timeObj.to_tm;
      return toSimpleMilitaryTime(to) <= toSimpleMilitaryTime(from);
    };

    const isOutOfTimeWindow = (timeObj: IRawTimeOccurrenceDatatype | IChangedBusyTime) => {
      let from = !_4.isString(timeObj.from_tm) ? this.momentToString(timeObj.from_tm) : timeObj.from_tm;
      let to = !_4.isString(timeObj.to_tm) ? this.momentToString(timeObj.to_tm) : timeObj.to_tm;
      from = toSimpleMilitaryTime(from);
      to = toSimpleMilitaryTime(to);
      const hourBegin = timeRange.hourBegin * 100;
      const hourEnd = timeRange.hourEnd * 100 + 59;
      return from < hourBegin || from > hourEnd || to < hourBegin || to > hourEnd;
    };

    const errorList = {
      isAnyPropertyNotSet: _4.some(potentialTime, (timeProperty: any) => timeProperty === 'Invalid date'),
      isStartTimeAfterEnd: isStartTimeAfterEndTime(potentialTime),
      isOutOfTimeWindow: isOutOfTimeWindow(potentialTime)
    };

    const errorKeys = _4.keys(_4.pickBy(errorList, (val: boolean) => val === true));

    _4.each(errorKeys, (errorKey: string) => {
      if (!_4.isUndefined(BusyTimeErrorMessage[errorKey])) {
        errors.found.push(BusyTimeErrorMessage[errorKey].message);
      }
    });

    return errors.found;
  }
}

angular
  .module('ui-ng-calendar')
  .factory(
    'ngTimeOccurrenceServant',
    function (
      Interface: any,
      TimeOccurrenceDTD: any,
      ngPhxCalendarConfiguration: PhxCalendarConfiguration,
      TimeOccurrenceBuilder: any,
      DayOfTheWeekEnum: IDayOfTheWeekEnum
    ): any {
      return new TimeOccurrenceServant(
        TimeOccurrenceDTD,
        Interface,
        ngPhxCalendarConfiguration,
        TimeOccurrenceBuilder,
        DayOfTheWeekEnum
      );
    }
  );



// WEBPACK FOOTER //
// ./app/vendor/ui-ng-calendar/src/servants/time/time_occurrence_servant.ts